import { useGetAiWorkersByCategoryQuery, useTypedSelector, categoryType, useDeleteAiWorkersMutation, useDeleteVoiceAgentMutation, RouteState, useEntityResourcePermissions } from '@circuitry-ai/doc-data';
import {
    CardListViewMode,
    CardUI,
    checkType,
    CtyButton,
    CtyShowToast,
    EntityListView,
    ListActionsCellWithDelete,
    SimpleDeleteConfirmationModal,
    getActionsColumnDef,
    getDateColumnDef,
    getDescriptionColumnDef,
    getNameColumnDef,
} from '@circuitry-ai/doc-ui';
import { Icon } from '@iconify/react';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, Tooltip, useDisclosure } from '@nextui-org/react';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AgentViewTabs } from './agentview';

function getVoiceAgentIdFromAgent(agent: { aiworkers_attrs?: Array<{ attr_name?: string; attr_value?: string }> } | undefined): string | null {
    const id = agent?.aiworkers_attrs?.find((a) => a?.attr_name === 'voice_agent_id')?.attr_value;
    return id?.trim() ? id.trim() : null;
}

function useDeleteAgentHandler(agent: any, refetchAgentList: () => void) {
    const { t } = useTranslation();
    const [deleteAiWorkers, { isLoading: deleteAgentLoading }] = useDeleteAiWorkersMutation();
    const [deleteVoiceAgent, { isLoading: deleteVoiceAgentLoading }] = useDeleteVoiceAgentMutation();

    const isVoiceAgentSubType = String(agent?.sub_type ?? '').trim().toLowerCase() === 'voice agent';
    const voiceAgentAssistantId = getVoiceAgentIdFromAgent(agent);

    const handleConfirmDelete = useCallback(async (closeModal: () => void) => {
        try {
            if (isVoiceAgentSubType) {
                if (!voiceAgentAssistantId) {
                    CtyShowToast(checkType.error, t('Voice agent is missing linked assistant id.'));
                    return;
                }
                await deleteVoiceAgent(voiceAgentAssistantId).unwrap();
            }
            const response = await deleteAiWorkers(agent?.aiworker_id);
            CtyShowToast(checkType.success, response?.data);
            closeModal();
            refetchAgentList();
        } catch (error) {
            CtyShowToast(checkType.error, (error as Error)?.message ?? 'Delete Operation failed');
        }
    }, [agent?.aiworker_id, deleteAiWorkers, deleteVoiceAgent, isVoiceAgentSubType, refetchAgentList, t, voiceAgentAssistantId]);

    return { handleConfirmDelete, isDeleteLoading: deleteAgentLoading || deleteVoiceAgentLoading };
}

const AgentActionButtonsCell = ({
    agent,
    onNavigate,
    refetchAgentList,
}: {
    readonly agent: any;
    readonly onNavigate: (agent: any, tab: string) => void;
    readonly refetchAgentList: () => void;
}) => {
    const { t } = useTranslation();
    const { canRead, canUpdate, canDelete } = useEntityResourcePermissions('agent', agent?.aiworker_id);
    const { handleConfirmDelete, isDeleteLoading } = useDeleteAgentHandler(agent, refetchAgentList);

    return (
        <ListActionsCellWithDelete
            actions={[
                {
                    label: t('Edit'),
                    onPress: () => onNavigate(agent, AgentViewTabs.DEFINITION),
                    variant: 'bordered',
                    color: 'default',
                    isDisabled: !canUpdate,
                },
                {
                    label: t('Execute'),
                    onPress: () => onNavigate(agent, AgentViewTabs.EXECUTE),
                    variant: 'solid',
                    color: 'primary',
                    isDisabled: !canRead,
                },
            ]}
            canDelete={canDelete}
            entityName={agent?.name ?? ''}
            onConfirmDelete={handleConfirmDelete}
            isDeleteLoading={isDeleteLoading}
            deleteConfirmLabel={t('Delete Agent')}
        />
    );
};

const agentActionLinkClass = 'inline-flex items-center text-base font-medium text-foreground underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:opacity-40 disabled:no-underline';

const AgentCardItem = ({ agent, socketUrl, refetchAgentList }: { readonly agent: any; readonly socketUrl: string; readonly refetchAgentList: () => void }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
    const { canRead, canUpdate, canDelete } = useEntityResourcePermissions('agent', agent?.aiworker_id);
    const { handleConfirmDelete, isDeleteLoading } = useDeleteAgentHandler(agent, refetchAgentList);

    const handleNavigation = (tab: string) => {
        const agentData = { ...agent, socketUrl };
        navigate(`/agents/${RouteState.UPDATE}/${agentData.aiworker_id}?tab=${tab}`, { state: { agent: agentData } });
    };

    const description = agent?.description ?? '';

    return (
        <>
            <div id={agent.aiworker_id} className="relative flex flex-col gap-1.5 rounded-xl border border-divider bg-content1 px-[18px] pb-4 pt-5 shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-shadow hover:shadow-[0_6px_20px_rgba(0,0,0,0.1)]">
                <Dropdown>
                    <DropdownTrigger>
                        <Button variant="light" isIconOnly aria-label={t('Agent actions')} className="absolute right-2 top-2 z-10 h-7 w-7 min-w-0 text-default-500">
                            <Icon icon="lucide:ellipsis-vertical" width="16" height="16" />
                        </Button>
                    </DropdownTrigger>
                    <DropdownMenu aria-label="Agent Actions" disabledKeys={canDelete ? [] : ['delete']}>
                        <DropdownItem key="delete" className="text-danger" onPress={onOpen}>
                            Delete
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
                <div className="truncate pr-7 text-sm font-medium leading-[21px] text-foreground" title={agent?.name}>{agent?.name}</div>
                <div className="min-h-[42px]">
                    <Tooltip
                        content={description}
                        placement="top"
                        showArrow
                        isDisabled={!description}
                        classNames={{ content: 'max-w-xs' }}
                    >
                        <p className="m-0 line-clamp-2 w-fit max-w-full text-sm leading-[21px] text-default-500">
                            {description}
                        </p>
                    </Tooltip>
                </div>
                <div className="mt-1 flex items-center gap-0.5 border-t border-divider pt-3">
                    <button
                        type="button"
                        className={agentActionLinkClass}
                        disabled={!canUpdate}
                        onClick={() => handleNavigation('definition')}
                    >
                        Configure
                        <Icon icon="lucide:square-pen" width="12" height="12" className="ml-1 opacity-60" />
                    </button>
                    <span className="mx-1 h-4 w-px shrink-0 bg-divider" aria-hidden="true" />
                    <button
                        type="button"
                        className={`${agentActionLinkClass} ml-auto`}
                        disabled={!canRead}
                        onClick={() => handleNavigation(AgentViewTabs.EXECUTE)}
                    >
                        Execute
                        <Icon icon="lucide:arrow-up-right" width="12" height="12" className="ml-1 opacity-60" />
                    </button>
                </div>
            </div>
            <SimpleDeleteConfirmationModal
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                entityName={agent?.name ?? ''}
                onConfirm={() => handleConfirmDelete(onClose)}
                isConfirmLoading={isDeleteLoading}
                cancelLabel='Close'
                confirmLabel='Delete Agent'
            />
        </>
    );
};

const AgentsCardsList = [
    {
        name: "Automation",
        id: 'automation',
        description: 'Choose the applications and AI abilities you`d like to integrate with your Advisor.',
        path: '/agents/automation',
    }
];

const renderAgentActionsCell = (handleNavigation: (agent: any, tab: string) => void, refetchAgentList: () => void) =>
    function AgentActionsRenderer(params: any) {
        return (
            <AgentActionButtonsCell agent={params?.data} onNavigate={handleNavigation} refetchAgentList={refetchAgentList} />
        );
    };

export function AgentsList() {
    const token = useTypedSelector((state) => state.auth.token);
    const [skip, setSkip] = useState<boolean>(true);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [viewMode, setViewMode] = useState<CardListViewMode>(CardListViewMode.GRID);

    const { data: agentsList, isLoading, isFetching, error, refetch } = useGetAiWorkersByCategoryQuery({ category: categoryType.AGENT }, { skip, refetchOnMountOrArgChange: true });
    const agentsResponse = agentsList as any;
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        if (token && skip) {
            setSkip(false);
        }
    }, [skip, token]);

    const allAgents = useMemo(() => agentsResponse?.agents ?? [], [agentsResponse]);
    const socketUrl = agentsResponse?.agent_websocket_url ?? '';

    const filteredAgents = useMemo(() => {
        if (!searchQuery.trim()) return allAgents;
        const q = searchQuery.toLowerCase();
        return allAgents.filter((agent: any) => agent.name?.toLowerCase().includes(q));
    }, [allAgents, searchQuery]);

    const handleNavigation = useCallback((agent: any, tab: string) => {
        const agentData = { ...agent, socketUrl };
        navigate(`/agents/${RouteState.UPDATE}/${agentData.aiworker_id}?tab=${tab}`, { state: { agent: agentData } });
    }, [navigate, socketUrl]);

    const columnDefs = useMemo(
        () => [
            getNameColumnDef(t('Agent Name')),
            getDescriptionColumnDef(t('Public Description')),
            getDateColumnDef(t('Last Modified')),
            getActionsColumnDef({
                headerName: t('Actions'),
                maxWidth: 280,
                cellRenderer: renderAgentActionsCell(handleNavigation, refetch),
            }),
        ],
        [handleNavigation, t, refetch]
    );

    if (allAgents.length === 0 && !isLoading && !isFetching && !error) {
        return (
            <div className="flex-1 flex items-center justify-center h-full border-2 border-default-300 rounded-lg m-4 bg-default-100">
                <div className="text-2xl text-primary">{t('No Agents found')}</div>
            </div>
        );
    }

    return (
        <EntityListView
            items={filteredAgents}
            isLoading={isLoading || isFetching}
            error={error}
            searchId="agents-search"
            searchPlaceholder={t('Search agents')}
            onSearch={setSearchQuery}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            emptyMessage={t('No Agents found')}
            cardKey={(agent: any) => agent.aiworker_id}
            renderCard={(agent: any) => (
                <AgentCardItem agent={agent} socketUrl={socketUrl} refetchAgentList={refetch} />
            )}
            extraGridChildren={AgentsCardsList.map((agent: any, index: number) => (
                <CardUI
                    key={`${agent.id}-${index}`}
                    view={agent?.canView ?? false}
                    id={agent.id}
                    name={agent.name}
                    description={agent.description}
                    footerItems={
                        <CtyButton
                            className={'w-full border-1'}
                            variant={'solid'}
                            radius="sm"
                            size={'lg'}
                            color={'primary'}
                            onPress={() => navigate(agent.path)}
                        >
                            {t('Launch')}
                        </CtyButton>
                    }
                />
            ))}
            tableId="agents-list-table"
            columnDefs={columnDefs}
        />
    );
}

export default AgentsList;
